// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import { CustomEditorTemplateProps, ImageMapPickerDefinition } from "@guestsupply/CustomFieldDefinition";
import { color } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import Modal from "@insite/mobius/Modal";
import Select from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import RichTextEditor from "@insite/shell/Components/Elements/RichTextEditor";
import ShellState from "@insite/shell/Store/ShellState";
import Tippy from "@tippyjs/react";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

import { inlinePositioning } from "tippy.js";

type OwnProps = CustomEditorTemplateProps<ImageMapPickerValue[], ImageMapPickerDefinition>;

const mapStateToProps = (state: ShellState, ownProps: OwnProps) => {
    const coordinates = state.data.pages.widgetsById[(ownProps.item as any).id]
        ? state.data.pages.widgetsById[(ownProps.item as any).id].fields.coords
        : [];
    return {
        coordinates,
    };
};

interface ImageMapPickerValue {
    xPos: number;
    yPos: number;
    xPercentage: any;
    yPercentage: any;
    productId: string;
    pointColor: string;
    tippyContent: string;
    content: string;
    openOnload: boolean;
}

type State = {
    imageLoaded: boolean;
    coords: ImageMapPickerValue[];
    selectedCoords: ImageMapPickerValue;
    imageUrl: string;
    imageElement: any;
    tempSku: string;
    tempCoords: ImageMapPickerValue[];
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasToasterContext;

class ImageMapPickerField extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            imageLoaded: false,
            coords: [],
            selectedCoords: {
                xPos: 0,
                yPos: 0,
                xPercentage: 0,
                yPercentage: 0,
                productId: "",
                pointColor: "#780011",
                tippyContent: "",
                content: "",
                openOnload: false,
            },
            imageUrl: "",
            imageElement: null,
            tempSku: "",
            tempCoords: [],
        };
    }

    static getDerivedStateFromProps(props: Props, state: State) {
        if (props.item.fields.imageUrl !== state.imageUrl) {
            return {
                imageUrl: props.item.fields.imageUrl,
            };
        }
        return null;
    }

    componentDidMount() {
        const imageElement = document.getElementById(`image_${this.state.imageUrl}`);
        this.setState({
            imageElement,
        });
        this.setState({ coords: this.props.fieldValue });
    }

    componentDidUpdate(_prevProps: Props, prevState: State) {
        if (!this.state.imageElement && this.state.imageLoaded && this.state.imageUrl) {
            this.setImageElement();
        }

        if (prevState.imageUrl !== this.state.imageUrl) {
            this.props.updateField(this.props.fieldDefinition.name, []);
            this.setState({ coords: [] });
        }
    }

    setImageElement = () => {
        const imageElement = document.getElementById(`image_${this.state.imageUrl}`);
        this.setState({
            imageElement,
        });
    };

    closeModalHandler = () => {
        this.props.toaster.addToast({
            body: translate("Hotspot Modal is closed succesfully"),
            messageType: "info",
        });
        this.setState({ imageLoaded: false, tempCoords: [] });
    };

    getCoordinates = (e: any) => {
        if (this.state.tempCoords.length === 10) {
            this.props.toaster.addToast({ body: translate("Can't Add more than 10 points"), messageType: "danger" });
            return;
        }
        let posX = 0;
        let posY = 0;
        let percentageX = 0;
        let percentageY = 0;
        let event = e;

        let coords: ImageMapPickerValue[] = this.state.tempCoords;

        const imgPosY = e.target.getBoundingClientRect().top;
        const imgPosX = e.target.getBoundingClientRect().left;
        if (!event) {
            event = window.event;
        }
        if (e.pageX || e.pageY) {
            posX = event.pageX;
            posY = event.pageY;
        } else if (event.clientX || event.clientY) {
            posX = event.clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
            posY = event.clientY + document.body.scrollTop + document.documentElement.scrollTop;
        }

        posX = posX - imgPosX;
        posY = posY - imgPosY;
        percentageX = (posX / 680) * 100;
        percentageY = (posY / 500) * 100;

        coords = [
            ...coords,
            {
                xPos: posX,
                yPos: posY,
                xPercentage: percentageX,
                yPercentage: percentageY,
                productId: "",
                pointColor: "#780011",
                tippyContent: "",
                content: "",
                openOnload: false,
            },
        ];
        this.setState({ tempCoords: coords });
    };

    handleProductSelection = (e: any, index: number) => {
        const coords = [...this.state.coords];
        const coord = {} as ImageMapPickerValue;
        coord.pointColor = coords[index].pointColor;
        coord.productId = e.target.value;
        coord.tippyContent = coords[index].tippyContent;
        coord.content = coords[index].content;
        coord.xPos = coords[index].xPos;
        coord.yPos = coords[index].yPos;
        coord.yPercentage = coords[index].yPercentage;
        coord.xPercentage = coords[index].xPercentage;
        coord.openOnload = coords[index].openOnload;

        coords[index] = { ...coord };
        this.setState({ coords });
        this.props.updateField(this.props.fieldDefinition.name, coords);
    };

    handleContentChange = (e: any, index: number) => {
        const coords = [...this.state.coords];
        const coord = {} as ImageMapPickerValue;
        coord.pointColor = coords[index].pointColor;
        coord.productId = coords[index].productId;
        coord.content = e;
        coord.tippyContent = coords[index].tippyContent;
        coord.xPos = coords[index].xPos;
        coord.yPos = coords[index].yPos;
        coord.yPercentage = coords[index].yPercentage;
        coord.xPercentage = coords[index].xPercentage;
        coord.openOnload = coords[index].openOnload;

        coords[index] = { ...coord };
        this.setState({ coords });
        this.props.updateField(this.props.fieldDefinition.name, coords);
    };

    handlePointColorSelection = (e: any, index: number) => {
        const coords = [...this.state.coords];
        const coord = {} as ImageMapPickerValue;
        coord.pointColor = e.target.value;
        coord.productId = coords[index].productId;
        coord.tippyContent = coords[index].tippyContent;
        coord.content = coords[index].content;
        coord.xPos = coords[index].xPos;
        coord.yPos = coords[index].yPos;
        coord.yPercentage = coords[index].yPercentage;
        coord.xPercentage = coords[index].xPercentage;
        coord.openOnload = coords[index].openOnload;
        coords[index] = { ...coord };
        this.setState({ coords });
        this.props.updateField(this.props.fieldDefinition.name, coords);
    };

    openModal = () => {
        this.setState({ imageLoaded: true, tempCoords: this.state.coords });
    };

    savePoints = () => {
        const coords = this.state.tempCoords;
        let cancelSaving = false;
        for (let i = 0; i < coords.length; i++) {
            if (!coords[i].tippyContent.length) {
                this.props.toaster.addToast({
                    body: translate("Hotspot point content type must be selected"),
                    messageType: "danger",
                });
                cancelSaving = true;
                break;
            }
            if (coords[i].tippyContent === TippyContent.productSpot && !coords[i].productId.length) {
                this.props.toaster.addToast({
                    body: translate("Hotspot point PRODUCT ERP # must be filled"),
                    messageType: "danger",
                });
                cancelSaving = true;

                break;
            }
        }
        if (cancelSaving) {
            return;
        }
        this.props.toaster.addToast({ body: translate("Hotspot changes have been saved"), messageType: "info" });
        this.setState({ coords: this.state.tempCoords });
        this.props.updateField(this.props.fieldDefinition.name, this.state.tempCoords);
        this.setState({ imageLoaded: false, tempCoords: [] });
    };

    removePoint = (index: number) => {
        const newCoords = [...this.state.coords];
        newCoords.splice(index, 1);
        this.props.updateField(this.props.fieldDefinition.name, newCoords);
        this.setState({ coords: newCoords });
    };

    removeModalPoint = (index: number) => {
        const newCoords = [...this.state.tempCoords];
        newCoords.splice(index, 1);
        this.setState({ tempCoords: newCoords });
    };

    handleFallbackState = (coords: ImageMapPickerValue[]) => {
        this.setState({ tempCoords: coords });
    };

    checkBoxChangeHandler = ({ value, index }: { value: boolean; index: number }) => {
        const newCoords = [...this.state.coords];
        newCoords[index] = { ...newCoords[index], openOnload: value };

        this.setState({ coords: newCoords });
        this.props.updateField(this.props.fieldDefinition.name, newCoords);
    };

    render() {
        const { imageLoaded, coords, imageUrl, tempCoords } = this.state;
        if (!imageUrl) {
            return null;
        }

        return (
            <StyledWrapper
                css={css`
                    margin: 20px 0;
                `}
            >
                <Typography
                    css={css`
                        margin: 20px 0;
                        font-weight: bold;
                    `}
                    transform="uppercase"
                >
                    {this.props.fieldDefinition.displayName}
                </Typography>
                <Button
                    css={css`
                        margin-bottom: 15px;
                        margin-top: 10px;
                    `}
                    onClick={this.openModal}
                    aria-label="Open Image Modal"
                >
                    {translate("Open Image Modal")}
                </Button>
                {coords &&
                    coords.length > 0 &&
                    coords.map((coord, index) => (
                        <StyledWrapper
                            key={`${coord.xPos}_${coord.yPos}`}
                            css={css`
                                display: flex;
                                flex-wrap: wrap;
                                justify-content: space-between;
                                margin-bottom: 15px;
                            `}
                        >
                            <Typography
                                size="15px"
                                lineHeight="18px"
                                weight="bold"
                                css={css`
                                    width: 100%;
                                    margin-bottom: 10px;
                                `}
                            >{`POINT ${index + 1}`}</Typography>
                            <TextField
                                disabled={true}
                                cssOverrides={{
                                    formField: css`
                                        max-width: 50%;
                                        padding-right: 20px;
                                    `,
                                }}
                                type="text"
                                value={coord.xPos}
                                label="X"
                            />
                            <TextField
                                disabled={true}
                                cssOverrides={{
                                    formField: css`
                                        max-width: 50%;
                                        padding-left: 20px;
                                    `,
                                }}
                                type="text"
                                value={coord.yPos}
                                label="Y"
                            />
                            <StyledWrapper
                                css={css`
                                    margin-top: 10px;
                                    width: 100%;
                                `}
                            >
                                {coord.tippyContent === TippyContent.productSpot ? (
                                    <TextField
                                        cssOverrides={{
                                            formField: css`
                                                margin-top: 10px;
                                            `,
                                        }}
                                        value={coord.productId}
                                        type="text"
                                        label={translate("Product ERP #")}
                                        onChange={e => this.handleProductSelection(e, index)}
                                    />
                                ) : coord.tippyContent === TippyContent.richContentSpot ? (
                                    <RichTextEditor
                                        value={coord.content}
                                        name="text"
                                        placeholder={translate("Tooltip Content")}
                                        onChange={e => this.handleContentChange(e, index)}
                                    />
                                ) : null}
                            </StyledWrapper>
                            <Checkbox
                                checked={coord.openOnload}
                                onChange={(_, value) => {
                                    this.checkBoxChangeHandler({ value, index });
                                }}
                                css={css`
                                    margin-top: 10px;
                                `}
                            >
                                {translate("Open On Page Load")}
                            </Checkbox>
                            <Button
                                css={css`
                                    margin-top: 10px;
                                `}
                                onClick={() => this.removePoint(index)}
                                aria-label="Remove Point"
                            >
                                {translate("Remove Point")}
                            </Button>
                        </StyledWrapper>
                    ))}
                <Modal size={710} isOpen={imageLoaded} handleClose={this.closeModalHandler}>
                    <StyledWrapper
                        css={css`
                            width: 667px;
                            height: 500px;
                            margin-bottom: 10px;
                            position: relative;
                            img {
                                aspect-ratio: 667 / 500;
                            }
                        `}
                    >
                        <img
                            onMouseDown={this.getCoordinates}
                            id={`image_${imageUrl}`}
                            src={imageUrl}
                            alt={translate("Not Found Image")}
                            width="667px"
                            height="500px"
                        />
                        {tempCoords &&
                            tempCoords.length > 0 &&
                            tempCoords.map((coord, index) => {
                                // eslint-disable-next-line react/display-name
                                const PointItem = React.forwardRef((_props, refs) => (
                                    <StyledWrapper
                                        ref={refs as any}
                                        css={css`
                                            width: 100%;
                                            height: 100%;
                                            display: flex;
                                            justify-content: center;
                                            align-items: center;
                                            cursor: pointer;
                                        `}
                                    >
                                        <Typography size="13px" lineHeight="15px" weight="bold">
                                            {index + 1}
                                        </Typography>
                                    </StyledWrapper>
                                ));

                                return (
                                    <PinPoint
                                        key={`${coord.xPos}-${coord.yPos}`}
                                        pointColor={coord.pointColor}
                                        xPos={coord.xPos}
                                        yPos={coord.yPos}
                                    >
                                        <Tippy
                                            inlinePositioning={true}
                                            plugins={[inlinePositioning]}
                                            interactive={true}
                                            trigger="click"
                                            showOnCreate={coord?.openOnload}
                                            offset={[0, 8]}
                                            content={
                                                <ProductSku
                                                    coord={coord}
                                                    index={index}
                                                    coords={tempCoords}
                                                    fallBackState={this.handleFallbackState}
                                                    removeItem={this.removeModalPoint}
                                                />
                                            }
                                            placement="auto"
                                            popperOptions={{
                                                modifiers: [
                                                    {
                                                        name: "flip",
                                                        options: {
                                                            fallbackPlacements: ["left", "start"],
                                                        },
                                                    },
                                                ],
                                            }}
                                        >
                                            <PointItem />
                                        </Tippy>
                                    </PinPoint>
                                );
                            })}
                    </StyledWrapper>
                    <Button
                        css={css`
                            background: blue;
                        `}
                        onClick={this.savePoints}
                        aria-label="Save"
                    >
                        {translate("Save")}
                    </Button>
                </Modal>
            </StyledWrapper>
        );
    }
}

const PinPoint = styled.div<any>`
    position: absolute;
    top: ${({ yPos }) => `calc(${yPos}px - 8px)`};
    left: ${({ xPos }) => `calc(${xPos}px - 8px)`};
    background: ${({ pointColor }) => pointColor};
    border-radius: 50%;
    height: 16px;
    width: 16px;
    display: flex;
    align-items: center;
    justify-content: center;

    .tippy-box {
        background: white;
        border-radius: 8px;
        padding: 10px;
        min-width: 200px;
    }
    .tippy-arrow:before {
        color: ${color("common.ui1")};
    }
`;

export default connect(mapStateToProps)(withToaster(ImageMapPickerField));

export const enum TippyContent {
    productSpot = "product",
    richContentSpot = "richContent",
}

const ProductSku: React.FC<{
    coord: ImageMapPickerValue;
    coords: ImageMapPickerValue[];
    index: number;
    fallBackState: (coords: ImageMapPickerValue[]) => void;
    removeItem: (index: number) => void;
}> = ({ coord, fallBackState, index, removeItem, coords }) => {
    const [sku, setSku] = React.useState(coord.productId);
    const [tippyContent, setTippyContent] = React.useState(coord.tippyContent);
    const [pointColor, setPointColor] = React.useState(coord.pointColor);

    const handleSkuChange = (e: any) => {
        setSku(e.target.value);
    };

    const handleColorChange = (e: any) => {
        setPointColor(e.target.value);
    };
    const handleDropDownChange = (e: any) => {
        setTippyContent(e.currentTarget.value);
    };

    const handleBlur = (e: any) => {
        const newCoords = [...coords];
        let newCoord = {} as ImageMapPickerValue;
        newCoord = { ...coord };
        newCoord.pointColor = pointColor;
        newCoord.productId = sku;
        newCoord.tippyContent = tippyContent;
        newCoords[index] = { ...newCoord };
        fallBackState(newCoords);
    };

    return (
        <StyledWrapper>
            <Select
                label={translate("Product Spot or a Rich Content Spot")}
                placeholder="Select"
                value={tippyContent}
                onChange={handleDropDownChange}
            >
                <option value="">{translate("Select Content")}</option>
                <option value={TippyContent.richContentSpot}>{translate("Rich Content Spot")}</option>
                <option value={TippyContent.productSpot}>{translate("Product Spot")}</option>
            </Select>
            {tippyContent === TippyContent.productSpot && (
                <TextField
                    cssOverrides={{
                        formField: css`
                            margin-top: 15px;
                        `,
                    }}
                    value={sku}
                    type="text"
                    label={translate("Product ERP #")}
                    onChange={handleSkuChange}
                />
            )}

            <TextField
                type="color"
                cssOverrides={{
                    formField: css`
                        margin-top: 15px;
                    `,
                }}
                value={pointColor}
                onChange={handleColorChange}
                label="Point Color"
            />
            <Button
                variant="tertiary"
                css={css`
                    margin-top: 15px;
                    margin-right: 15px;
                `}
                onClick={() => removeItem(index)}
                aria-label="Remove Point"
            >
                {translate("Remove Point")}
            </Button>
            <Button
                variant="primary"
                css={css`
                    margin-top: 15px;
                `}
                onClick={handleBlur}
                aria-label="Save"
            >
                {translate("Save")}
            </Button>
        </StyledWrapper>
    );
};
